import React, { useState, useEffect, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import clsx from "clsx"

import styles from "./notifications.module.css"
import HeaderTitle from "../../../components/header/headerTitle/headerTitle"
import { useInstitution } from "../../../context/InstitutionContext"
import { useNotificationAction } from "../../../context/NotificationActionContext"
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  archiveAllNotifications
} from "../../../services/notifications"
import {
  getIconInfo,
  formatTimeAgo,
  getActionPlan
} from "../../../components/notifications/notificationActions"
import NotificationDisciplines from "../../../components/notifications/NotificationDisciplines"
import NotificationMessage from "../../../components/notifications/notificationMessage"

// Limite alto para trazer "todas" as notificações numa única página (o backend não pagina ainda).
// ponytail: sem paginação — se o volume crescer muito, adicionar "carregar mais" aqui.
const PAGE_LIMIT = 100

const Notifications = () => {
  const { institution } = useInstitution()
  const { triggerAction } = useNotificationAction()
  const navigate = useNavigate()
  const location = useLocation()

  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [onlyMine, setOnlyMine] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)

  const fetchNotifications = useCallback(async () => {
    if (!institution?.id) {
      setLoading(false)
      return
    }
    setLoading(true)
    const data = await getNotifications(institution.id, { limit: PAGE_LIMIT, onlyMine, archived: showArchived })
    setNotifications(data.notifications || [])
    setUnreadCount(data.unreadCount || 0)
    setLoading(false)
  }, [institution?.id, onlyMine, showArchived])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const handleMarkAllRead = async () => {
    if (!institution?.id) return
    await markAllNotificationsAsRead(institution.id)
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
    setUnreadCount(0)
  }

  const handleClearAll = async () => {
    if (!institution?.id) return
    await archiveAllNotifications(institution.id)
    setNotifications([])
    setUnreadCount(0)
  }

  const markAsReadLocal = async (notification) => {
    if (notification.isRead) return
    await markNotificationAsRead(notification.id)
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
    )
    setUnreadCount((prev) => Math.max(0, prev - 1))
  }

  const handleToggleExpand = (notification) => {
    markAsReadLocal(notification)
    setExpandedId((prev) => (prev === notification.id ? null : notification.id))
  }

  const parseData = (notification) => {
    try {
      return typeof notification.data === "string"
        ? JSON.parse(notification.data)
        : notification.data
    } catch {
      return null
    }
  }

  const handleAction = (notification, plan) => {
    markAsReadLocal(notification)

    if (plan.actionKind === "navigate" && plan.actionUrl) {
      navigate(plan.actionUrl)
      return
    }

    if (plan.actionKind === "mapping") {
      triggerAction({
        type: notification.type,
        notificationId: notification.id,
        data: parseData(notification)
      })
    }

    if (!location.pathname.includes("/importar-dados")) {
      navigate("/app/importar-dados")
    }
  }

  return (
    <>
      <HeaderTitle title="Notificações">
        <div className={styles.headerActions}>
          <button
            type="button"
            className={clsx(styles.filterOnlyMine, onlyMine && styles.filterOnlyMineActive)}
            onClick={() => setOnlyMine((v) => !v)}
          >
            {onlyMine ? "Mostrando só minhas" : "Só minhas"}
          </button>
          {!showArchived && unreadCount > 0 && (
            <button type="button" className={styles.markAllRead} onClick={handleMarkAllRead}>
              Marcar todas como lidas
            </button>
          )}
          {!showArchived && notifications.length > 0 && (
            <button type="button" className={styles.clearBtn} onClick={handleClearAll}>
              Limpar notificações
            </button>
          )}
          <button
            type="button"
            className={clsx(styles.filterOnlyMine, showArchived && styles.filterOnlyMineActive)}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? "← Ativas" : "Arquivadas"}
          </button>
        </div>
      </HeaderTitle>

      <div className={styles.page}>
        {loading ? (
          <div className={styles.stateBox}>
            <span className={styles.stateText}>Carregando notificações…</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.stateBox}>
            <span className={styles.stateIcon}>🔔</span>
            <span className={styles.stateText}>
              {showArchived ? "Nenhuma notificação arquivada" : "Nenhuma notificação"}
            </span>
          </div>
        ) : (
          <ul className={styles.list}>
            {notifications.map((n) => {
              const iconInfo = getIconInfo(n.type)
              const plan = getActionPlan(n)
              const isExpanded = expandedId === n.id
              return (
                <li
                  key={n.id}
                  className={clsx(styles.item, !n.isRead && styles.unread)}
                >
                  <div className={styles.itemMain} onClick={() => handleToggleExpand(n)}>
                    <div className={clsx(styles.icon, styles[iconInfo.className])}>
                      {iconInfo.emoji}
                    </div>
                    <div className={styles.content}>
                      <div className={styles.titleRow}>
                        <p className={styles.title}>{n.title}</p>
                        {!n.isRead && <span className={styles.unreadDot} />}
                      </div>
                      {n.userDisplayName && (
                        <span className={styles.owner}>{n.userDisplayName}</span>
                      )}

                      <p className={styles.sectionLabel}>Descrição</p>
                      <NotificationMessage text={n.message} collapsed={!isExpanded} />

                      {plan && (
                        <>
                          <p className={styles.sectionLabel}>Plano de ação</p>
                          <button
                            type="button"
                            className={clsx(styles.planSummary, isExpanded && styles.planSummaryOpen)}
                            aria-expanded={isExpanded}
                          >
                            <span className={styles.planSummaryChevron} aria-hidden="true">›</span>
                            <span>{plan.summary}</span>
                          </button>
                          {isExpanded && (
                            <>
                              <ol className={styles.planSteps}>
                                {plan.steps.map((step, idx) => (
                                  <li key={idx}>{step}</li>
                                ))}
                              </ol>
                              <button
                                type="button"
                                className={styles.planActionButton}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAction(n, plan)
                                }}
                              >
                                {plan.actionLabel}
                              </button>
                            </>
                          )}
                        </>
                      )}

                      <span className={styles.time}>{formatTimeAgo(n.createdAt)}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <NotificationDisciplines disciplines={parseData(n)?.disciplines} />
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </>
  )
}

export default Notifications
